// Werke am Space (Welle 5, V5.1): Lesen, Säubern und Prüfen der Liste im Editor.
// Die Prüfung spiegelt das Backend (pruefeWerke, samePlant) nur, damit der Nutzer die Ursache
// an der Zeile sieht. Verbindlich bleibt die Antwort des Servers.
//
// Kennungen werden NIE normalisiert: „0040“ bleibt „0040“, auch beim Vergleich auf Dubletten.
// Sie stecken literal in Paket-IDs, Ordnernamen und Artefakt-IDs.

export const WERK_KENNUNG = /^[A-Za-z0-9_.-]{1,32}$/;
// Spiegel von VERBOTENE_KENNUNGEN in backend/lib/space-werke.js: passen auf das Muster, sind als
// Objektschlüssel aber gefährlich und werden vom Server abgewiesen.
export const VERBOTENE_KENNUNGEN = new Set(["__proto__", "constructor", "prototype"]);
export const MAX_SPACE_WERKE = 50; // gleich MAX_WERKE in backend/lib/space-werke.js (be-werke Korrekturrunde 1)
export const MAX_WERK_NAME = 120;

// Alle bekannten Formate lesen: {name,kennung}, "Name__97", "97" (wie parseInstanceWerke).
export function toRows(list) {
  if (!Array.isArray(list)) return [];
  return list.flatMap(w => {
    if (typeof w === "string") {
      if (w.includes("__")) {
        const [name, kennung] = w.split("__");
        return [{ name: name || "", kennung: kennung || "" }];
      }
      return [{ name: w, kennung: w }];
    }
    if (!w || typeof w !== "object") return [];
    return [{ name: String(w.name ?? ""), kennung: String(w.kennung ?? "") }];
  });
}

// Komplett leere Zeilen fallen still weg, der Name wird getrimmt. Die Kennung bleibt, wie sie
// eingegeben wurde; ein Leerzeichen darin meldet die Prüfung, statt es heimlich zu entfernen.
export function cleanRows(rows) {
  return (rows || [])
    .map(r => ({ name: String(r?.name ?? "").trim(), kennung: String(r?.kennung ?? "") }))
    .filter(r => r.name || r.kennung.trim());
}

const numericPlant = value => /^\d+$/.test(value) ? value.replace(/^0+(?=\d)/, "") : null;

// Ergebnis: { ok, rows: { [index]: { kennung?: {code,...}, name?: {code} } }, list: {code}|null }
// Die Indizes beziehen sich auf die angezeigten Zeilen. Komplett leere Zeilen werden übersprungen,
// weil cleanRows sie beim Speichern ohnehin entfernt.
export function validateRows(rows) {
  const out = {};
  const mark = (idx, feld, fehler) => { out[idx] = { ...(out[idx] || {}), [feld]: fehler }; };
  const literal = new Map();
  const numerisch = new Map();
  (rows || []).forEach((r, idx) => {
    const kennung = String(r?.kennung ?? "");
    if (!kennung.trim() && !String(r?.name ?? "").trim()) return;
    if (String(r?.name ?? "").length > MAX_WERK_NAME) mark(idx, "name", { code: "nameZuLang" });
    if (!kennung) { mark(idx, "kennung", { code: "kennungLeer" }); return; }
    if (!WERK_KENNUNG.test(kennung) || VERBOTENE_KENNUNGEN.has(kennung)) { mark(idx, "kennung", { code: "kennungUngueltig" }); return; }
    if (literal.has(kennung)) { mark(idx, "kennung", { code: "kennungDoppelt", kennung }); return; }
    literal.set(kennung, idx);
    const zahl = numericPlant(kennung);
    if (zahl !== null) {
      if (numerisch.has(zahl)) {
        mark(idx, "kennung", { code: "kennungMehrdeutig", kennung, andere: numerisch.get(zahl) });
        return;
      }
      numerisch.set(zahl, kennung);
    }
  });
  const list = cleanRows(rows).length > MAX_SPACE_WERKE ? { code: "zuViele" } : null;
  return { ok: !list && Object.keys(out).length === 0, rows: out, list };
}

export function sameRows(a, b) {
  return JSON.stringify(cleanRows(a)) === JSON.stringify(cleanRows(b));
}

const hasItems = v => Array.isArray(v) && v.length > 0;
export function differingCompare(compare) {
  return (Array.isArray(compare) ? compare : [])
    .filter(c => c && (hasItems(c.onlyHere) || hasItems(c.missing) || hasItems(c.renamed)));
}

export function werkLabel(w) {
  const name = String(w?.name ?? "").trim();
  const kennung = String(w?.kennung ?? "");
  return name && name !== kennung ? `${name} (${kennung})` : kennung || name;
}
